import {
  DeleteEnvironmentDto,
  EnvironmentDto,
  EnvironmentState,
  RequestId,
  RequestIds,
  RequestState,
  UpdateEnvironmentDto,
  EnvironmentRequest
} from 'vrlcm-gui-types';
import { Http, safeJsonParse } from 'vrlcm-gui-utils';
import { LcopsApiUri } from './uri';

export class EnvironmentApi {
  public static async getAllEnvironmentRequests() {
    const response = await Http.get<EnvironmentState[]>(
      LcopsApiUri.Environment.GET_ALL_ENVIRONMENT_REQUESTS_URL
    );
    return response.data;
  }

  public static async getAllEnvironmentsByStatus(status: string) {
    const url = LcopsApiUri.EnvironmentV2.ENVIRONMENTS_URL;
    const searchParams = new URLSearchParams();
    searchParams.append('status', status);
    const response = await Http.get<EnvironmentRequest[]>(
      `${url}?${searchParams.toString()}`
    );
    return response.data;
  }

  public static async getAllCloudEnvironmentRequests() {
    const response = await Http.get<EnvironmentState[]>(
      LcopsApiUri.Environment.GET_CLOUD_PROXY_ENVIRONMENT_REQUESTS
    );
    return response.data;
  }

  public static async getAllEnvironmentRequestsWithState(state: RequestState) {
    const environmentRequests = await EnvironmentApi.getAllEnvironmentRequests();
    return environmentRequests.filter(e => e.requestState === state);
  }

  public static async getAllEnvironmentsWithState(state: RequestState) {
    const environmentRequests = await EnvironmentApi.getAllEnvironmentRequestsWithState(
      state
    );
    return environmentRequests.map(e => e.environmentRequestDTO);
  }

  public static async getEnvironmentDataById(environmentId: string) {
    const response = await Http.get<EnvironmentDto>(
      LcopsApiUri.EnvironmentV2.ENVIRONMENT_URL(environmentId)
    );
    return response.data;
  }

  public static async getEnvironmentRequestById(environmentId: string) {
    const environmentDto = await EnvironmentApi.getEnvironmentDataById(
      environmentId
    );
    const environmentRequest = safeJsonParse<EnvironmentRequest>(
      environmentDto.environmentData
    );
    return environmentRequest!;
  }

  public static async renameEnvironment(
    updateEnvironmentDto: UpdateEnvironmentDto
  ) {
    const response = await Http.patch<EnvironmentDto>(
      LcopsApiUri.EnvironmentV2.ENVIRONMENTS_URL,
      updateEnvironmentDto
    );
    return response.data;
  }

  public static async deleteEnvironment(
    environmentId: string,
    options: DeleteEnvironmentDto
  ) {
    const response = await Http.delete<RequestId>(
      LcopsApiUri.EnvironmentV2.ENVIRONMENT_URL(environmentId),
      {
        data: options
      }
    );
    return response.data.requestId;
  }

  public static async deleteEnvironmentRequest(
    environmentId: string,
    requestId: string
  ) {
    await Http.delete(
      LcopsApiUri.EnvironmentV2.ENVIRONMENT_REQUEST_URL(
        environmentId,
        requestId
      )
    );
  }

  public static async getRedeployRequestData(requestId: string) {
    const response = await Http.get<EnvironmentState>(
      LcopsApiUri.Environment.GET_REDEPLOY_REQUEST_DATA_URL(requestId)
    );
    return response.data;
  }

  public static async triggerAllEnvironmentsInventorySync() {
    const response = await Http.post<RequestIds>(
      LcopsApiUri.Environment.TRIGGER_ALL_ENVIRONMENT_INVENTORY_SYNC_URL
    );
    return response.data;
  }

  public static getVidmClusterHealth = async () => {
    const response = await Http.get(
      LcopsApiUri.Environment.GET_VIDM_CLUSTER_HEALTH
    );
    return response.data;
  };

  public static getVidmClusterAutoRecovery = async () => {
    const response = await Http.get(
      LcopsApiUri.Environment.GET_VIDM_CLUSTER_AUTO_RECOVERY
    );
    return response.data;
  };

  public static pauseOrResumeVidmClusterHealth = async (enable: boolean) => {
    const response = await Http.post(
      LcopsApiUri.Environment.RESUME_VIDM_CLUSTER_HEALTH_NOTIFICATION(enable)
    );
    return response.data.requestId;
  };

  public static async vraInternalNetworkReassign(data: {
    environmentId: string;
    productId: string;
    productVersion: string;
    vraK8ClusterCidr: string;
    vraK8ServiceCidr: string;
    preValidate: boolean;
  }) {
    const response = await Http.post<{
      requestId: string;
    }>(
      LcopsApiUri.Environment.VRA_INTERNAL_NETWORK_RANGE_REASSIGN(
        data.environmentId,
        data.productId
      ),
      data
    );
    return response.data;
  }

  public static async toggleCEIP(environmentId: string, ceipValue: boolean) {
    const response = await Http.post<{
      requestId: string;
    }>(LcopsApiUri.EnvironmentV2.TOGGLE_CEIP(environmentId), {
      enableTelemetry: ceipValue
    });
    return response.data.requestId;
  }

  public static async integrateVIDB(environmentId: string, body: any) {
    const response = await Http.post<{
      requestId: string;
    }>(LcopsApiUri.Environment.INTEGRATE_VMWARESSO(environmentId), body);
    return response.data.requestId;
  }

  public static async toggleVCF(
    environmentId: string,
    productToLbMap: string,
    turnOffVcfEnabledEnvironment: boolean
  ) {
    const response = await Http.post<{ requestId: string }>(
      LcopsApiUri.EnvironmentV2.TOGGLE_VCF(environmentId),
      {
        productToLbMap: productToLbMap,
        turnOffVcfEnabledEnvironment: turnOffVcfEnabledEnvironment
      }
    );
    return response.data.requestId;
  }

  public static async validateRefreshToken(
    environmentId: string,
    environmentName: string,
    refreshToken: string
  ) {
    const response = await Http.post(
      LcopsApiUri.EnvironmentV2.VALIDATE_REFRESH_TOKEN,
      {
        environmentId,
        environmentName,
        refreshToken
      }
    );

    return response.data;
  }
}
